import React, { useState, useRef, useEffect } from 'react';
import { Model } from '../types/chat';

interface ModelSelectorProps {
  models: Model[];
  selectedModel: Model | null;
  onModelSelect: (model: Model) => void;
  isLoading?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  selectedModel,
  onModelSelect,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fonction pour obtenir la couleur du badge de confidentialité
  const getConfidentialityColor = (confidentiality?: string) => {
    if (!confidentiality) return { bg: '#f3f4f6', text: '#6b7280' };

    switch (confidentiality.toLowerCase()) {
      case 'public':
        return { bg: '#dcfce7', text: '#166534' };
      case 'internal':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'confidential':
        return { bg: '#fecaca', text: '#991b1b' };
      case 'restricted':
        return { bg: '#e0e7ff', text: '#3730a3' };
      default:
        return { bg: '#f3f4f6', text: '#6b7280' };
    }
  };

  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleModelSelect = (model: Model) => {
    onModelSelect(model);
    setIsOpen(false);
  };

  return (
    <div style={{
      padding: '1rem',
      borderBottom: '1px solid #e1e5e9',
      backgroundColor: 'white',
      borderLeft: 'none',
      display: 'flex',
      justifyContent: 'flex-start'
    }}>
      <div ref={dropdownRef} style={{ position: 'relative', width: 'auto', minWidth: '300px', maxWidth: '500px' }}>
        {/* Custom dropdown button */}
        <button
          onClick={() => !isLoading && setIsOpen(!isOpen)}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            backgroundColor: 'white',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            color: '#374151',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.borderColor = '#9ca3af';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
            }
          }}
        >
          <div style={{ flex: 1 }}>
            {selectedModel ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <div style={{ fontWeight: '600', color: '#111827' }}>
                    {selectedModel.name || selectedModel.id || 'Unknown Model'}
                  </div>
                  {selectedModel.metadata?.confidentiality && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: getConfidentialityColor(selectedModel.metadata.confidentiality).bg,
                      color: getConfidentialityColor(selectedModel.metadata.confidentiality).text,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {selectedModel.metadata.confidentiality}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.3' }}>
                  {(selectedModel.metadata?.description || selectedModel.description) || 'No description available'}
                </div>
              </div>
            ) : (
              <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>Loading models...</div>
            )}
          </div>

          {/* Dropdown arrow */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              marginLeft: '8px'
            }}
          >
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>

        {/* Custom dropdown menu */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 50,
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {models.map((model) => (
              <button
                key={model.id}
                onClick={() => handleModelSelect(model)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '4px'
                }}>
                  <div style={{ fontWeight: '500', color: '#374151' }}>
                    {model.name || model.id || 'Unknown Model'}
                  </div>
                  {model.metadata?.confidentiality && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: getConfidentialityColor(model.metadata.confidentiality).bg,
                      color: getConfidentialityColor(model.metadata.confidentiality).text,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {model.metadata.confidentiality}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                  {(model.metadata?.description || model.description) || 'No description available'}
                </div>
                {/* Afficher d'autres métadonnées si disponibles */}
                {model.metadata && Object.entries(model.metadata).some(([key]) => key !== 'confidentiality' && key !== 'description') && (
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Object.entries(model.metadata)
                      .filter(([key]) => key !== 'confidentiality' && key !== 'description')
                      .map(([key, value]) => (
                        <span
                          key={key}
                          style={{
                            fontSize: '9px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            fontWeight: '500'
                          }}
                        >
                          {key}: {value}
                        </span>
                      ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSelector;